<template>
  <div class="auth-page">
    <div class="auth-card">
      <div class="auth-brand">
        <div class="auth-brand__title">{{ isForgotMode ? 'Сброс пароля' : 'Вход в аккаунт' }}</div>
        <div class="auth-brand__subtitle">
          {{
            isForgotMode
              ? 'Укажите email, и мы отправим письмо для восстановления доступа'
              : 'Продолжите работу со стримами и публикациями'
          }}
        </div>
      </div>

      <form v-if="!isForgotMode" class="auth-form" @submit.prevent="login">
        <div class="mb-3">
          <label for="username" class="form-label">Email</label>
          <input
            id="username"
            v-model.trim="username"
            type="email"
            class="form-control"
            placeholder="user@example.com"
            autocomplete="email"
            required
          />
        </div>

        <div class="mb-3">
          <label for="password" class="form-label">Пароль</label>
          <input
            id="password"
            v-model="password"
            type="password"
            class="form-control"
            placeholder="Введите пароль"
            autocomplete="current-password"
            required
          />
        </div>

        <div v-if="error" class="alert alert-danger py-2 mb-3">{{ error }}</div>

        <button class="btn btn-primary w-100" type="submit" :disabled="loading">
          <span v-if="loading" class="spinner-border spinner-border-sm me-2"></span>
          Войти
        </button>

        <button class="auth-link-btn" type="button" @click="switchToForgotMode">
          Забыли пароль?
        </button>

        <div class="auth-footer">
          Нет аккаунта?
          <router-link to="/register">Зарегистрироваться</router-link>
        </div>
      </form>

      <form v-else class="auth-form" @submit.prevent="requestPasswordReset">
        <div class="mb-3">
          <label for="forgot-email" class="form-label">Email</label>
          <input
            id="forgot-email"
            v-model.trim="forgotEmail"
            type="email"
            class="form-control"
            placeholder="user@example.com"
            autocomplete="email"
            required
          />
        </div>

        <div v-if="forgotSuccess" class="alert alert-success py-2 mb-3">{{ forgotSuccess }}</div>
        <div v-if="forgotError" class="alert alert-danger py-2 mb-3">{{ forgotError }}</div>

        <button class="btn btn-primary w-100" type="submit" :disabled="forgotLoading">
          <span v-if="forgotLoading" class="spinner-border spinner-border-sm me-2"></span>
          Отправить письмо
        </button>

        <div class="auth-footer auth-footer--inline">
          <button class="auth-link-btn auth-link-btn--inline" type="button" @click="switchToLoginMode">
            Вернуться ко входу
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'LoginForm',
  data() {
    return {
      username: '',
      password: '',
      error: '',
      loading: false,
      isForgotMode: false,
      forgotEmail: '',
      forgotError: '',
      forgotSuccess: '',
      forgotLoading: false,
    }
  },
  methods: {
    extractErrorMessage(err) {
      const message = err?.response?.data?.message || err?.response?.data?.error
      return String(message || 'Ошибка авторизации. Проверьте email и пароль.')
    },
    extractForgotError(err) {
      const message = err?.response?.data?.message || err?.response?.data?.error
      return String(message || 'Не удалось отправить письмо для сброса пароля.')
    },
    switchToForgotMode() {
      this.isForgotMode = true
      this.error = ''
      this.forgotError = ''
      this.forgotSuccess = ''
      this.forgotEmail = this.username
    },
    switchToLoginMode() {
      this.isForgotMode = false
      this.forgotError = ''
      this.forgotSuccess = ''
    },
    validateForgotEmail() {
      if (!this.forgotEmail) return 'Укажите email.'
      if (!EMAIL_REGEX.test(this.forgotEmail)) return 'Введите корректный email.'
      return ''
    },
    async requestPasswordReset() {
      this.forgotError = ''
      this.forgotSuccess = ''

      const validationError = this.validateForgotEmail()
      if (validationError) {
        this.forgotError = validationError
        return
      }

      this.forgotLoading = true
      try {
        const { data } = await axios.post(
          '/api/account/forgot-password',
          { email: this.forgotEmail }
        )
        this.forgotSuccess = String(data?.data || 'Письмо для сброса пароля отправлено.')
      } catch (err) {
        console.error('Ошибка запроса сброса пароля:', err)
        this.forgotError = this.extractForgotError(err)
      } finally {
        this.forgotLoading = false
      }
    },
    async login() {
      this.error = ''
      this.forgotError = ''
      this.forgotSuccess = ''
      this.loading = true
      try {
        const response = await axios.post('/api/account/authenticate', {
          username: this.username,
          password: this.password,
        })
        // astra returns SuccessResponse(true, jwt) → { success: true, data: jwt }
        const jwt = response.data?.data
        if (!jwt) throw new Error('No JWT in authentication response')
        localStorage.setItem('jwt', jwt)
        axios.defaults.headers.common.Authorization = `Bearer ${jwt}`
        await this.$store.dispatch('fetchCurrentUser')
        this.$router.push('/dashboard')
      } catch (err) {
        console.error('Ошибка авторизации:', err)
        this.error = this.extractErrorMessage(err)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 102px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background:
    radial-gradient(circle at 12% 20%, rgba(13, 110, 253, 0.12), transparent 40%),
    radial-gradient(circle at 85% 80%, rgba(25, 135, 84, 0.1), transparent 45%),
    #f7f9fd;
}

.auth-card {
  width: 100%;
  max-width: 460px;
  background: #fff;
  border: 1px solid #e8edf7;
  border-radius: 16px;
  box-shadow: 0 16px 40px rgba(17, 24, 39, 0.12);
  overflow: hidden;
}

.auth-brand {
  padding: 18px 20px 14px;
  background: linear-gradient(135deg, #1f3d8a, #2f67d6);
  color: #fff;
}

.auth-brand__title {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.25;
}

.auth-brand__subtitle {
  margin-top: 4px;
  font-size: 13px;
  opacity: 0.9;
}

.auth-form {
  padding: 18px 20px 20px;
}

.auth-footer {
  margin-top: 14px;
  font-size: 13px;
  color: #5f6f8b;
  text-align: center;
}

.auth-footer a {
  color: #1d5ed8;
  text-decoration: none;
  font-weight: 600;
}

.auth-footer a:hover {
  text-decoration: underline;
}

.auth-footer--inline {
  margin-top: 10px;
}

.auth-link-btn {
  margin-top: 10px;
  border: 0;
  background: none;
  color: #1d5ed8;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  padding: 0;
}

.auth-link-btn:hover {
  text-decoration: underline;
}

.auth-link-btn--inline {
  margin-top: 0;
}
</style>
